import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  DEFAULT_SIDEBAR_WIDTH,
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  useAppStore,
} from '../../store/appStore';
import { darkTheme } from '../../theme/tokens';

const t = darkTheme; // sidebar is always dark

// Keyboard nudge step for the resize handle (px per arrow press).
const RESIZE_KEY_STEP = 16;

interface SidebarResizeHandleProps {
  /** The X coordinate (viewport px) of the sidebar's LEFT edge — a drag maps cursor
   *  X → width as `clientX − left` since the sidebar grows rightward. Read at drag
   *  time (not once) so a live layout shift doesn't stale the mapping. */
  measureLeftEdge: () => number;
  /** Mid-drag flag for the host, so it can switch its width transition off and
   *  track the cursor 1:1. */
  onResizingChange: (resizing: boolean) => void;
}

/**
 * The drag-to-resize splitter on the expanded left sidebar's right edge (SC-3491).
 * Mirrors the chat surfaces' `ChatResizeHandle` (SC-2393) for an edge that grows
 * rightward: pointer drag, keyboard nudge (←/→/Home/End), double-click reset, and
 * the mid-drag-unmount teardown. Absolutely positioned, so the sidebar must be
 * `position: relative`. Width lives in `appStore` (`sidebarWidth`, clamped by the
 * setter).
 */
export function SidebarResizeHandle({ measureLeftEdge, onResizingChange }: SidebarResizeHandleProps) {
  const sidebarWidth = useAppStore((s) => s.sidebarWidth);
  const setSidebarWidth = useAppStore((s) => s.setSidebarWidth);
  const [handleActive, setHandleActive] = useState(false);
  // Holds the active drag's teardown so an unmount mid-drag (the sidebar collapsed,
  // the shell unmounted on logout) still removes the window listeners and body
  // styles the drag attached.
  const endResizeRef = useRef<(() => void) | null>(null);

  const startResize = (e: ReactPointerEvent) => {
    e.preventDefault();
    onResizingChange(true);
    const onMove = (ev: PointerEvent) => setSidebarWidth(ev.clientX - measureLeftEdge());
    const endResize = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', endResize);
      window.removeEventListener('pointercancel', endResize);
      document.body.style.removeProperty('user-select');
      document.body.style.removeProperty('cursor');
      onResizingChange(false);
      endResizeRef.current = null;
    };
    endResizeRef.current = endResize;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', endResize);
    window.addEventListener('pointercancel', endResize);
  };

  // Tear down an in-progress drag if the handle unmounts before pointerup.
  useEffect(() => () => endResizeRef.current?.(), []);

  // Keyboard a11y: → widens / ← narrows (the sidebar grows rightward), Home/End
  // jump to the bounds; double-click resets to the default width.
  const onResizeKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowRight') setSidebarWidth(sidebarWidth + RESIZE_KEY_STEP);
    else if (e.key === 'ArrowLeft') setSidebarWidth(sidebarWidth - RESIZE_KEY_STEP);
    else if (e.key === 'Home') setSidebarWidth(0); // clamps to min
    else if (e.key === 'End') setSidebarWidth(Infinity); // clamps to max
    else return;
    e.preventDefault();
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      aria-valuenow={sidebarWidth}
      aria-valuemin={SIDEBAR_MIN_WIDTH}
      aria-valuemax={SIDEBAR_MAX_WIDTH}
      tabIndex={0}
      onPointerDown={startResize}
      onDoubleClick={() => setSidebarWidth(DEFAULT_SIDEBAR_WIDTH)}
      onKeyDown={onResizeKey}
      onMouseEnter={() => setHandleActive(true)}
      onMouseLeave={() => setHandleActive(false)}
      onFocus={() => setHandleActive(true)}
      onBlur={() => setHandleActive(false)}
      style={{
        position: 'absolute',
        right: -3,
        top: 0,
        bottom: 0,
        width: 6,
        cursor: 'col-resize',
        touchAction: 'none',
        zIndex: 1,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 2,
          height: '100%',
          background: handleActive ? t.accent : 'transparent',
          transition: 'background 0.12s',
        }}
      />
    </div>
  );
}
